import { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { OpeningSideName } from "building-types/openings/openingTypes";
import { WindowLateralAlignment } from "building-types/openings/openingTypesEnum";
import type { Room } from "building-types/rooms/roomTypes";
import type { AwningSettings } from "building-types/settings/awningSettings";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { ExteriorWindowCasingSettings } from "building-types/settings/exteriorWindowCasingSettings";
import type { MergeSettingsForEntityParams } from "building-types/settings/mergeSettingsForEntityParams";
import {
  ShutterConfigurationType,
  ShutterType,
  ShutterVerticalAlignment,
  WindowMullionType,
} from "building-types/settings/settingsTypesEnum";
import type { ShutterViewNoteTable } from "building-types/settings/viewNotesSettings";
import type { WainscotingSettings } from "building-types/settings/wainscotingSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import type { WindowMaterialSettings } from "building-types/settings/windowMaterialSettings";
import type { WindowMullionSettings } from "building-types/settings/windowMullionSettings";
import type { WindowSettings } from "building-types/settings/windowSettings";
import type { WindowShutterSettings } from "building-types/settings/windowShutterSettings";
import { WindowPaneType, WindowShapeTypes } from "building-types/settings/windowTypesEnum";
import type { WallSideContext } from "building-types/walls/wallSideTypes";
import { ShutterStreetOrientationPreference } from "building-types/windows/shuttersTypes";
import type {
  MergedWindowSettings,
  GeneratedWindowPlacement,
  WindowDescriptor,
} from "building-types/windows/windowTypes";
import { assertIsNotNullOrUndefined, assertIsNotUndefined, ensureDefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { degreeToRadian } from "common/utils/math";
import { WINDOW_INTERIOR_FRAME_WIDTH } from "doc/elements/window/constants";
import { aabb2Grow } from "geom/aabb2";
import { aabb2New } from "geom/aabb2Types";
import type { Extrusion } from "geom/brep";
import { faceNew } from "geom/brep";
import { buildBSPNodeFromPolyLine2 } from "geom/bsp";
import type { ClipRailContext } from "geom/bspClipping";
import { clippingNodeNew, clipRail } from "geom/bspClipping";
import type { NodeBSP } from "geom/bspTypes";
import { DIST_EPSILON, ZERO_EPSILON } from "geom/core";
import { extrudeFaceAlongNormal, extrudeFace } from "geom/extrude";
import type { CurveCurvePointIntersection } from "geom/intersectLine2Line2";
import { intersectLine2Line2 } from "geom/intersectLine2Line2";
import type { Ray2PolyLine2Intersection } from "geom/intersectRay2";
import { intersectLine2Ray2 } from "geom/intersectRay2";
import { offsetPolyLine2, polyLine2Thicken } from "geom/offset";
import { planeCopy, planeNormal, planeMoveAlongNormal, planeRotateAroundU } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import {
  polyLine2Reverse,
  polyLine2AABB2,
  polyLine2ByTwoPoints,
  polyLine2ConcatAll,
  polyLine2Edge,
  polyLine2SliceByStartEnd,
  polyLine2Extend,
  polyLine2Close,
  polyLine2Area,
  polyLine2Length,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { rangeNonInclusiveByCount } from "geom/range";
import { ray2Eval } from "geom/ray2";
import { rect2ByLine2AndThickness } from "geom/rect2";
import { checkOrientationWithMode } from "../streetOrientation";
import {
  customCircularWindowGrid,
  customOrthogonalWindowGrid,
  customRadialWindowGrid,
  regularOrthogonalWindowGrid,
} from "./windowGrids";
import { getWindowHeight, windowHeadHeight } from "./windowHeightTools";
import {
  windowBackOpening2,
  windowBackOpening3,
  windowBackOpeningRect2,
  windowFrontOpening2,
  windowFrontOpening3,
  windowFrontOpeningRect2,
} from "./windowQuery";

export const GLASS_THICKNESS = 0.125;
const GRID_DEPTH = 0.5 + GLASS_THICKNESS;
export const WINDOW_FRAME_DEPTH = 3;
export const WINDOW_JAMB_THICKNESS = 0.5;
const WINDOW_SASH_FRAME_DEPTH = 1;
export const WINDOW_SASH_FRAME_DEPTH_HALF = WINDOW_SASH_FRAME_DEPTH / 2;
export const WINDOW_SASH_FRAME_WIDTH = 1.5;

export interface WindowGenerator {
  exportComponentName: string;
  sashFrame: Extrusion[];
  mullions: Extrusion[];
  glass: Extrusion[];
}

export interface PaneDescriptor {
  readonly outline: PolyLine2;
  readonly type: WindowPaneType;
  readonly exportComponentName?: string;
}

export interface WindowShuttersPlacement {
  size: number;
  bottom: number;
  height: number;
  settings: WindowShutterSettings | undefined;
  addLeft: boolean;
  addRight: boolean;
  viewNotes: ShutterViewNoteTable | undefined;
}

export function windowShuttersPlacementNew(
  size: number,
  bottom: number,
  height: number,
  settings: WindowShutterSettings | undefined,
  addLeft: boolean,
  addRight: boolean,
): WindowShuttersPlacement {
  return {
    size,
    bottom,
    height,
    settings,
    addLeft,
    addRight,
    viewNotes: settings?.viewNotes,
  };
}

export function makeDefaultWindowShuttersPlacementLegacy(
  shutters: MergedWindowSettings["shutters"],
  window: GeneratedWindowPlacement,
): WindowShuttersPlacement {
  let shutterSize = 0;

  if (shutters?.shutterType !== ShutterType.DISABLED) {
    shutterSize = shutters?.shutterWidthOverride ?? shutterSize;
  }

  const height = getWindowHeight(window.descriptor);

  const { verticalOffset, headHeight = height } = window;

  const windowBottom = verticalOffset + headHeight - height;

  return windowShuttersPlacementNew(shutterSize, windowBottom, height, shutters, true, true);
}

export function makeDefaultWindowShuttersPlacement(
  shutters: MergedWindowSettings["shutters"],
  window: GeneratedWindowPlacement,
  widthThreshold: number,
  mirror: boolean,
): WindowShuttersPlacement | null {
  if (shutters?.shutterType === ShutterType.DISABLED) {
    return null;
  }

  const shutterSize = shutters?.shutterWidthOverride ?? window.descriptor.width / 2;
  const shutterHeight = shutters?.shutterHeightOverride ?? window.descriptor.height;
  const verticalAlignment = shutters?.verticalAlignment ?? ShutterVerticalAlignment.CENTER;
  const verticalOffset = shutters?.verticalOffset ?? 0;
  const shutterOrientation = shutters?.orientation ?? ShutterStreetOrientationPreference.FRONT;

  const { streetOrientation: streetOrientationWindow } = window;

  switch (shutterOrientation) {
    case ShutterStreetOrientationPreference.FRONT:
      if (streetOrientationWindow === StreetOrientation.FRONT) {
        break;
      } else {
        return null;
      }
    case ShutterStreetOrientationPreference.NOT_BACK:
      if (streetOrientationWindow !== StreetOrientation.BACK) {
        break;
      } else {
        return null;
      }
    default:
    case ShutterStreetOrientationPreference.ALL:
      break;
  }

  const height = getWindowHeight(window.descriptor);
  const heightDelta = shutterHeight - height;

  const { verticalOffset: windowVerticalOffset, headHeight = height } = window;

  let windowBottom = windowVerticalOffset + headHeight - height + verticalOffset;

  switch (verticalAlignment) {
    case ShutterVerticalAlignment.CENTER:
      windowBottom -= heightDelta / 2;
      break;
    case ShutterVerticalAlignment.BOTTOM:
      // no change;
      break;
    case ShutterVerticalAlignment.TOP:
      windowBottom -= heightDelta;
      break;
    default:
      assertUnreachable(verticalAlignment);
  }

  if (
    window.descriptor.width + 2 * shutterSize > widthThreshold ||
    shutterSize < DIST_EPSILON ||
    shutterHeight < DIST_EPSILON
  ) {
    return null;
  }

  let shutterConfiguration = shutters?.shutterConfiguration ?? ShutterConfigurationType.BOTH;

  if (mirror && shutterConfiguration !== ShutterConfigurationType.BOTH) {
    shutterConfiguration =
      shutterConfiguration === ShutterConfigurationType.LEFT
        ? ShutterConfigurationType.RIGHT
        : ShutterConfigurationType.LEFT;
  }

  const addRight =
    shutterConfiguration === ShutterConfigurationType.BOTH || shutterConfiguration === ShutterConfigurationType.RIGHT;

  const addLeft =
    shutterConfiguration === ShutterConfigurationType.BOTH || shutterConfiguration === ShutterConfigurationType.LEFT;

  return windowShuttersPlacementNew(shutterSize, windowBottom, shutterHeight, shutters, addLeft, addRight);
}

interface WindowPaneProps {
  readonly shape: WindowShapeTypes;
  readonly gridMinY: number;
  readonly gridMaxY: number;
  readonly paneDescriptors: PaneDescriptor[];
  readonly plane: Plane;
  readonly mullionSettings: WindowMullionSettings;
  readonly streetOrientation: StreetOrientation;
}

export function generateWindowPanes(props: WindowPaneProps): WindowGenerator[] {
  const { shape, gridMinY, gridMaxY, paneDescriptors, plane, mullionSettings, streetOrientation } = props;
  //pane outline and plane
  const sashPlane = planeCopy(plane);
  const [nx, ny, nz] = planeNormal(sashPlane);
  const singlePane = paneDescriptors.length === 1;
  const gens: WindowGenerator[] = [];

  const moveSashPlane = (plane: Plane, val: number) => {
    plane.originX -= nx * val;
    plane.originY -= ny * val;
    plane.originZ -= nz * val;
  };

  const extVal = -((paneDescriptors.length * WINDOW_SASH_FRAME_DEPTH) / 2 - WINDOW_SASH_FRAME_DEPTH_HALF);
  moveSashPlane(sashPlane, extVal);

  for (let i = 0; i < paneDescriptors.length; i++) {
    const { outline: panePl, type: paneType } = paneDescriptors[i];
    const frontPane = i === 0;
    const gen: WindowGenerator = {
      exportComponentName: paneDescriptors[i].exportComponentName ?? paneType,
      sashFrame: [],
      mullions: [],
      glass: [],
    };

    makeWindowSash(shape, paneDescriptors[i], sashPlane, gen);

    const { mullionType } = mullionSettings;

    if (mullionType !== WindowMullionType.DISABLED) {
      const includeMullions =
        mullionSettings.mullionOrientation === undefined ||
        checkOrientationWithMode(streetOrientation, mullionSettings.mullionOrientation);

      if (includeMullions) {
        const { verticalThickness: mullionThickness } = mullionSettings;

        const makeMullions = (gen: WindowGenerator, gridEdges: PolyLine2[]) => {
          for (const edge of gridEdges) {
            const mull2 = polyLine2Thicken(edge, mullionThickness, true);
            const mull2Face = faceNew(
              [polyLine2Reverse(mull2)],
              planeMoveAlongNormal(planeCopy(sashPlane), -GRID_DEPTH / 2),
            );
            const mull2BRep = extrudeFaceAlongNormal(mull2Face, GRID_DEPTH);
            gen.mullions.push(mull2BRep);
          }
        };

        // make the Y bbox values consistent across panes for arched top windows with vertical
        // divisions to ensure the horizontal grids are aligned
        const { minX, minY, maxX, maxY } = polyLine2AABB2(panePl);
        let [resolvedMinY, resolvedMaxY] = [minY, maxY];
        if (paneType === WindowPaneType.FULL || paneType === WindowPaneType.TOP) {
          resolvedMaxY = gridMaxY;
        }
        if (paneType === WindowPaneType.FULL || paneType === WindowPaneType.LOW) {
          resolvedMinY = gridMinY;
        }
        const paneAABB = aabb2New(minX, resolvedMinY, maxX, resolvedMaxY);
        aabb2Grow(paneAABB, -WINDOW_SASH_FRAME_WIDTH, -WINDOW_SASH_FRAME_WIDTH);

        if (mullionType === WindowMullionType.ORTHOGONAL) {
          const { paneOneXCount, paneOneYCount, paneTwoXCount, paneTwoYCount, isLegacyWindowGrid } = mullionSettings;

          let [verticalCount, horizontalCount] =
            frontPane || singlePane ? [paneOneXCount, paneOneYCount] : [paneTwoXCount, paneTwoYCount];

          // check for legacy grid and single pane to accomodate legacy grid gen
          if (isLegacyWindowGrid && singlePane) {
            if (paneTwoXCount <= 1) {
              verticalCount = paneTwoXCount;
            }
            if (paneTwoYCount <= 1) {
              horizontalCount = paneTwoYCount;
            }
          }

          makeMullions(gen, regularOrthogonalWindowGrid(paneAABB, verticalCount, mullionThickness, true));
          makeMullions(gen, regularOrthogonalWindowGrid(paneAABB, horizontalCount, mullionThickness, false));
        } else if (mullionType === WindowMullionType.ORTHOGONAL_CUSTOM) {
          const { paneOneXLayout, paneOneYLayout, paneTwoXLayout, paneTwoYLayout } = mullionSettings;

          const [verticalPositions, horizontalPositions] =
            frontPane || singlePane ? [paneOneXLayout, paneOneYLayout] : [paneTwoXLayout, paneTwoYLayout];

          makeMullions(gen, customOrthogonalWindowGrid(paneAABB, verticalPositions, true));
          makeMullions(gen, customOrthogonalWindowGrid(paneAABB, horizontalPositions, false));
        } else if (mullionType === WindowMullionType.RADIAL_CUSTOM) {
          const { paneOneRadialLayout, paneOneCircularLayout } = mullionSettings;
          makeMullions(gen, customRadialWindowGrid(paneAABB, paneOneRadialLayout));
          makeMullions(gen, customCircularWindowGrid(paneAABB, paneOneCircularLayout));
        }
      }
    }

    moveSashPlane(sashPlane, WINDOW_SASH_FRAME_DEPTH);
    gens.push(gen);
  }

  return gens;
}

function makeWindowSash(shape: WindowShapeTypes, paneDescriptor: PaneDescriptor, plane: Plane, gen: WindowGenerator) {
  const { outline: sashPl, type: paneType } = paneDescriptor;
  const glassFace = faceNew([sashPl], planeMoveAlongNormal(planeCopy(plane), -GLASS_THICKNESS / 2));
  const glassThickness = extrudeFaceAlongNormal(glassFace, GLASS_THICKNESS);
  gen.glass.push(glassThickness);

  // inner offset - account for any shape inner offsets that create self intersecting polylines
  let innerPl: PolyLine2 | undefined;
  if (shape === WindowShapeTypes.HALF_ROUND) {
    innerPl = halfRoundInnerOffset(sashPl, WINDOW_SASH_FRAME_WIDTH);
  } else if (
    shape === WindowShapeTypes.RECTANGLE_ARCHED_TOP &&
    (paneType === WindowPaneType.TOP || paneType === WindowPaneType.FULL)
  ) {
    innerPl = offsetArchedPanel(sashPl, WINDOW_SASH_FRAME_WIDTH);
  } else {
    innerPl = offsetPolyLine2(sashPl, WINDOW_SASH_FRAME_WIDTH);
  }

  if (!innerPl) {
    return;
  }

  const glassFrame = faceNew(
    [sashPl, polyLine2Reverse(innerPl)],
    planeMoveAlongNormal(planeCopy(plane), -WINDOW_SASH_FRAME_DEPTH_HALF),
  );

  gen.sashFrame.push(extrudeFaceAlongNormal(glassFrame, WINDOW_SASH_FRAME_DEPTH));
}
interface VentExtrusionOptions {
  outline: PolyLine2;
  plane: Plane;
  divNum: number;
  thickness?: number;
  angle?: number;
  clipAgainstOutline?: boolean;
}

export function ventExtrusion({
  outline,
  plane,
  divNum,
  thickness = 1,
  angle = -45,
  clipAgainstOutline = false,
}: VentExtrusionOptions): Extrusion[] {
  const mullions: Extrusion[] = [];
  if (divNum < 2) {
    return mullions;
  }
  // make the divs equal the number of panels
  divNum -= 1;

  const VENT_FIN_THICKNESS = 0.25;
  const VENT_FACE_RECESS_DEPTH = -1;

  const ventFinPlane = planeCopy(plane);
  const [nx, ny] = planeNormal(ventFinPlane);

  ventFinPlane.originX -= nx * VENT_FACE_RECESS_DEPTH;
  ventFinPlane.originY -= ny * VENT_FACE_RECESS_DEPTH;

  const divs = rangeNonInclusiveByCount(0, 1, divNum);

  planeRotateAroundU(ventFinPlane, degreeToRadian(angle));
  const [rotnx, rotny, rotnz] = planeNormal(ventFinPlane);
  const { minX, minY, maxX, maxY } = polyLine2AABB2(outline);

  const outlineBSP: NodeBSP | null = clipAgainstOutline ? buildBSPNodeFromPolyLine2(outline) : null;

  const clipOptions: ClipRailContext = {
    clippingNodes: [clippingNodeNew(outlineBSP)],
  };

  for (const div of divs) {
    const [, firstY] = ray2Eval(0, minY, 0, maxY - minY, div);

    const clippedRails = clipRail({
      rail: polyLine2ByTwoPoints(minX, firstY, maxX, firstY),
      height: 0,
      options: clipOptions,
    });

    const pl = planeCopy(ventFinPlane);
    pl.originZ += firstY;

    for (const clippedRail of clippedRails) {
      const mull2 = rect2ByLine2AndThickness(clippedRail[0], 0, clippedRail[2], 0, thickness / 2, thickness / 2);
      const mull2Face = faceNew([mull2], pl);
      const mull2BRep = extrudeFace(
        mull2Face,
        VENT_FIN_THICKNESS * rotnx,
        VENT_FIN_THICKNESS * rotny,
        VENT_FIN_THICKNESS * rotnz,
      );
      mullions.push(mull2BRep);
    }
  }

  return mullions;
}

export function makeGlassPlane(exteriorFacePlane: Plane): Plane {
  const WINDOW_INSET = 1.5;

  const glassPlane = planeCopy(exteriorFacePlane);
  const [nx, ny, nz] = planeNormal(glassPlane);

  glassPlane.originX -= nx * WINDOW_INSET;
  glassPlane.originY -= ny * WINDOW_INSET;
  glassPlane.originZ -= nz * WINDOW_INSET;

  return glassPlane;
}

export interface MakeWindowUnitOptions extends MergeSettingsForEntityParams {
  window: GeneratedWindowPlacement;
  issues: BuildingGenerationIssue[];
  windowSettings: WindowSettings;
  windowMaterialSettings: WindowMaterialSettings;
  wallLength: number;
  wallThickness: number;
  wallSettings: WallSettings;
  windowMullionSettings: WindowMullionSettings;
  exteriorWindowCasingSettings: ExteriorWindowCasingSettings;
  wainscotingSettings: WainscotingSettings;
  awningSettings: AwningSettings;
  datumAlignmentOffset: number;
  exteriorWallMaterial?: ExteriorMaterial;
  streetOrientation?: StreetOrientation;
  mirror?: boolean;
  context: WallSideContext;
}

export function windowAlignment(
  window: GeneratedWindowPlacement,
  wallThickness: number,
  datumOffset: number,
): {
  frontRoom: Room | undefined;
  backRoom: Room | undefined;
  backFacePlane: Plane | undefined;
  frontFacePlane: Plane | undefined;
  backOutlineRect2: PolyLine2 | undefined;
  backOutline3: PolyLine3 | undefined;
  frontOutlineRect2: PolyLine2 | undefined;
  frontOutline3: PolyLine3 | undefined;
  backOutlineCasing3: PolyLine3 | undefined;
  frontOutlineCasing3: PolyLine3 | undefined;
  windowFrameCenterlinePlane: Plane;
  depthAlignmentOffset: number;
} {
  const { opening, reverse } = window;

  //window frame depth alignment
  let depthAlignmentOffset = wallThickness - datumOffset - window.lateralOffset;
  if (window.lateralAlignment === WindowLateralAlignment.CENTERLINE) {
    depthAlignmentOffset -= WINDOW_FRAME_DEPTH / 2;
  } else if (window.lateralAlignment === WindowLateralAlignment.INSIDE_FACE) {
    depthAlignmentOffset -= WINDOW_FRAME_DEPTH;
  }

  //window frame centerline plane
  const centerlineOffset = depthAlignmentOffset + WINDOW_FRAME_DEPTH / 2;

  if (reverse) {
    const exteriorFacePlane = opening?.frontPlane;
    assertIsNotUndefined(exteriorFacePlane);
    const windowFrameCenterlinePlane = planeMoveAlongNormal(planeCopy(exteriorFacePlane), -centerlineOffset);
    return {
      frontRoom: window.backRoom,
      backRoom: window.frontRoom,
      backFacePlane: exteriorFacePlane,
      frontFacePlane: opening?.backPlane,
      backOutlineRect2: windowFrontOpeningRect2(window),
      backOutline3: windowFrontOpening3(window, false),
      frontOutlineRect2: windowBackOpeningRect2(window),
      frontOutline3: windowBackOpening3(window, false),
      backOutlineCasing3: windowFrontOpening3(window),
      frontOutlineCasing3: windowBackOpening3(window),
      windowFrameCenterlinePlane,
      depthAlignmentOffset,
    };
  } else {
    const exteriorFacePlane = opening?.backPlane;
    assertIsNotUndefined(exteriorFacePlane);
    const windowFrameCenterlinePlane = planeMoveAlongNormal(planeCopy(exteriorFacePlane), -centerlineOffset);
    return {
      frontRoom: window.frontRoom,
      backRoom: window.backRoom,
      backFacePlane: opening?.backPlane,
      frontFacePlane: opening?.frontPlane,
      backOutlineRect2: windowBackOpeningRect2(window),
      backOutline3: windowBackOpening3(window, false),
      frontOutlineRect2: windowFrontOpeningRect2(window),
      frontOutline3: windowFrontOpening3(window, false),
      backOutlineCasing3: windowBackOpening3(window),
      frontOutlineCasing3: windowFrontOpening3(window),
      windowFrameCenterlinePlane,
      depthAlignmentOffset,
    };
  }
}

export function windowConstrainAngleVerticalOffset(
  descriptor: Pick<WindowDescriptor, "angleVerticalOffset" | "height" | "width">,
): number {
  // protect against invalid values
  let verticalOffset = descriptor.angleVerticalOffset ?? 0;
  if (verticalOffset < 0) {
    return 0;
  }
  const h = descriptor.height;
  const w = descriptor.width;
  const hypot = Math.sqrt(h * h + w * w);
  const d = WINDOW_INTERIOR_FRAME_WIDTH;
  if (w - d < DIST_EPSILON || h < DIST_EPSILON) {
    return 0;
  }
  const z = (d * hypot) / h;
  const x = h - d - z;
  if (x < 0) {
    return 0;
  }
  const limit = (w * x) / (w - d);
  if (verticalOffset > limit) {
    verticalOffset = limit;
  }

  return verticalOffset;
}

export function windowCrossLevelCutPlane(
  window: GeneratedWindowPlacement,
  levelAboveId: string | undefined,
  levelAboveElevation: number | undefined,
  levelBelowId: string | undefined,
  levelBelowElevation: number | undefined,
  cutPlaneElevation: number,
): boolean {
  let top: number | undefined;
  if (!window) {
    return false;
  }
  const windowLevel = window.frontRoom?.level.id;

  if (levelAboveId && windowLevel === levelAboveId) {
    top = ensureDefined(levelAboveElevation) + windowHeadHeight(window);
  } else if (levelBelowId && windowLevel === levelBelowId) {
    top = ensureDefined(levelBelowElevation) + windowHeadHeight(window);
  }

  if (top) {
    const bottom = top - getWindowHeight(window.descriptor);
    if (bottom - DIST_EPSILON < cutPlaneElevation && top + DIST_EPSILON > cutPlaneElevation) {
      return true;
    }
  }
  return false;
}

export function halfRoundInnerOffset(pl: PolyLine2, dist: number): PolyLine2 {
  //the base should be the first edge in the polyline
  const len = pl.length;
  const base = polyLine2New([pl[0], pl[1], pl[2], pl[3]]);
  const arc = pl.slice(2, len);

  const baseOffset = offsetPolyLine2(base, dist);
  const bX1 = baseOffset[0];
  const bY1 = baseOffset[1];
  const bX2 = baseOffset[2];
  const bY2 = baseOffset[3];
  const arcOffset = offsetPolyLine2(arc, dist);

  let startX: number | undefined = undefined;
  let startY: number | undefined = undefined;
  let startSplit: number | undefined = undefined;
  let endX: number | undefined = undefined;
  let endY: number | undefined = undefined;
  let endSplit: number | undefined = undefined;

  //get intersection at start
  for (let i = 0; i < arcOffset.length - 4; i += 2) {
    const intStart = intersectLine2Line2(
      bX1,
      bY1,
      bX2,
      bY2,
      arcOffset[i],
      arcOffset[i + 1],
      arcOffset[i + 2],
      arcOffset[i + 3],
    );

    if (intStart) {
      startX = (intStart as CurveCurvePointIntersection).x;
      startY = (intStart as CurveCurvePointIntersection).y;
      startSplit = i + 2;
      break;
    }
  }

  //get intersection at end
  for (let i = arcOffset.length - 4; i > 0; i -= 2) {
    const intEnd = intersectLine2Line2(
      bX1,
      bY1,
      bX2,
      bY2,
      arcOffset[i],
      arcOffset[i + 1],
      arcOffset[i + 2],
      arcOffset[i + 3],
    );

    if (intEnd) {
      endX = (intEnd as CurveCurvePointIntersection).x;
      endY = (intEnd as CurveCurvePointIntersection).y;
      endSplit = i + 2;
      break;
    }
  }

  /**
   * The casting below is to preserve existing behavior and allow strict-null checking, which allows Nan/undefined values in the output.
   */
  const halfRoundOffset = polyLine2ConcatAll([
    [endX as number, endY as number],
    [startX as number, startY as number],
    arcOffset.slice(startSplit, endSplit),
    [endX as number, endY as number],
  ]);

  return halfRoundOffset;
}

/**
 * Assumes that the left edge of the panel polyline is the first edge and that the polyline is in CW order.
 */
function offsetArchedPanel(pl: PolyLine2, dist: number) {
  const leftEdge = polyLine2Edge(pl, 0);
  const rightEdge = polyLine2Edge(pl, -2);
  const arcPl = polyLine2SliceByStartEnd(pl, 1, -2);
  assertIsNotNullOrUndefined(arcPl);
  const offsetPanel = offsetInnerArchedPanel(leftEdge, arcPl, rightEdge, dist, false);
  polyLine2Extend(offsetPanel, -dist, -dist);
  return polyLine2Close(offsetPanel);
}

/**
 * Used for creating the inner panel of arched panel geometry like a shutter
 * or vertically divided arched top window.
 */
export function offsetInnerArchedPanel(
  leftEdge: PolyLine2,
  arcPl: PolyLine2,
  rightEdge: PolyLine2,
  dist: number,
  close: boolean = false,
): PolyLine2 {
  const leftEdgeOffset = offsetPolyLine2(leftEdge, dist);
  const arcOffset = offsetPolyLine2(arcPl, dist);
  const rightEdgeOffset = offsetPolyLine2(rightEdge, dist);

  let startSplit = 0;
  let endSplit = arcOffset.length - 1;

  //get intersection at left
  for (let i = 0; i < arcOffset.length - 4; i += 2) {
    const intStart = intersectLine2Line2(
      leftEdgeOffset[0],
      leftEdgeOffset[1],
      leftEdgeOffset[2],
      leftEdgeOffset[3],
      arcOffset[i],
      arcOffset[i + 1],
      arcOffset[i + 2],
      arcOffset[i + 3],
    );

    if (intStart) {
      leftEdgeOffset[2] = (intStart as CurveCurvePointIntersection).x;
      leftEdgeOffset[3] = (intStart as CurveCurvePointIntersection).y;
      startSplit = i + 2;
      break;
    }
  }

  //get intersection at right
  for (let i = arcOffset.length - 4; i > 0; i -= 2) {
    const intEnd = intersectLine2Line2(
      rightEdgeOffset[0],
      rightEdgeOffset[1],
      rightEdgeOffset[2],
      rightEdgeOffset[3],
      arcOffset[i],
      arcOffset[i + 1],
      arcOffset[i + 2],
      arcOffset[i + 3],
    );

    if (intEnd) {
      rightEdgeOffset[0] = (intEnd as CurveCurvePointIntersection).x;
      rightEdgeOffset[1] = (intEnd as CurveCurvePointIntersection).y;
      endSplit = i + 2;
      break;
    }
  }

  return polyLine2ConcatAll([leftEdgeOffset, arcOffset.slice(startSplit, endSplit), rightEdgeOffset], close);
}

/**
 * Used specifically for splitting the arched geometry of a window with vertical rays
 * for things like shutters and vertical divisions.
 * The ray is assumed to intersect the arc only once.
 */
export function splitArcPlRay(
  arcPl: PolyLine2,
  originX: number,
  originY: number,
  dirX: number,
  dirY: number,
): [PolyLine2, PolyLine2] | null {
  for (let i = 0; i < arcPl.length - 2; i += 2) {
    const x0 = arcPl[i];
    const y0 = arcPl[i + 1];
    const x1 = arcPl[i + 2];
    const y1 = arcPl[i + 3];

    const res = intersectLine2Ray2(
      x0,
      y0,
      x1,
      y1,
      originX,
      originY,
      dirX,
      dirY,
      ZERO_EPSILON,
    ) as Ray2PolyLine2Intersection;

    if (res) {
      const leftArcPl = polyLine2ConcatAll([arcPl.slice(0, i + 2), [res.x, res.y]]);
      const rightArcPl = polyLine2ConcatAll([[res.x, res.y], arcPl.slice(i + 2)]);
      return [leftArcPl, rightArcPl];
    }
  }

  return null;
}

export function archRadiusEqualsWindowWidth(window: WindowDescriptor) {
  const halfWindowWidth = window.width / 2;
  const archRadius = window.archRadius ?? halfWindowWidth;
  return Math.abs(archRadius - halfWindowWidth) < DIST_EPSILON;
}

export function isWindowDescriptor(descriptor: any): descriptor is WindowDescriptor {
  return (descriptor as WindowDescriptor)?.catalogItemType === "WINDOW_DESCRIPTOR";
}

export function windowPlacementArea(
  window: GeneratedWindowPlacement,
  side: OpeningSideName,
  includeCasing: boolean = true,
) {
  const openingPolyline =
    side === "BACK" ? windowBackOpening2(window, includeCasing) : windowFrontOpening2(window, includeCasing);

  if (!openingPolyline) {
    return 0;
  }

  return polyLine2Area(openingPolyline);
}

export function windowPlacementPerimeter(
  window: GeneratedWindowPlacement,
  side: OpeningSideName,
  includeCasing: boolean = true,
) {
  const openingPolyline =
    side === "BACK" ? windowBackOpening2(window, includeCasing) : windowFrontOpening2(window, includeCasing);

  if (!openingPolyline) {
    return 0;
  }

  return polyLine2Length(openingPolyline);
}
